import { Text, Flex, Icon } from '@vibe/core';
import { Inbox } from '@vibe/icons';
import { getUptimeColor, dayToDate } from '../services/analytics';
import { ResponsiveContainer, Bar, BarChart, XAxis, Cell, Tooltip as ChartTooltip } from 'recharts';

type DailyEntry = {
  date: string;
  uptime_percentage: number;
};

type DashboardUptimeStatsProps = {
  daily: DailyEntry[];
};

export function DashboardUptimeStats({ daily }: DashboardUptimeStatsProps) {
  // Take the last 14 days. Days are UTC (status-page buckets by SQLite's date()), and today is only
  // included once it is at least 2 hours old, so a near-empty day doesn't show a misleading percentage.
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  const includeToday = now.getUTCHours() >= 2;
  const filtered = daily.filter((d) => d.date < today || (includeToday && d.date === today)).slice(-14);

  const chartData = filtered.map((d) => {
    const date = dayToDate(d.date);
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    return {
      day: d.date,
      name: formattedDate,
      barValue: 100,
      uptimePercentage: d.uptime_percentage,
      color: getUptimeColor(d.uptime_percentage),
    };
  });

  return (
    <div
      style={{
        width: '100%',
        height: '50px',
      }}
    >
      {chartData.length === 0 ? (
        <div
          style={{
            overflow: 'hidden',
            width: 'calc(100% - 2px)',
            height: 'calc(100% - 10px)',
            border: '1px solid #d0d4e4',
            borderRadius: '4px',
          }}
        >
          <Flex
            gap={8}
            direction="row"
            align="center"
            justify="center"
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: '#fcfcfcff',
              color: '#7e7f93ff',
            }}
          >
            <Icon icon={Inbox} size={16} useCurrentColor />
            <Text type="text3" color="secondary">
              No data available
            </Text>
          </Flex>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            tabIndex={-1}
            data={chartData}
            barSize={8}
            margin={{
              top: 5,
              right: 10,
              bottom: -20,
              left: 0,
            }}
            style={{
              fontFamily: 'inherit',
              fontSize: '12px',
            }}
          >
            <XAxis dataKey="day" tick={false} tickLine={false} />

            <ChartTooltip
              cursor={{ fill: 'rgba(0, 0, 0, 0.03)' }}
              wrapperStyle={{
                marginTop: 20,
                boxShadow: '0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
                fontFamily: 'inherit',
                fontSize: 12,
              }}
              contentStyle={{
                backgroundColor: '#fff',
                borderRadius: 4,
                border: '1px solid #e0e0e0',
                padding: '8px 10px 2px 10px',
              }}
              labelStyle={{
                marginBottom: 4,
                fontWeight: 600,
              }}
              formatter={() => null}
              labelFormatter={(_date, [meta]) => {
                const percentage = meta?.payload?.uptimePercentage;
                return `${meta?.payload?.name} → ${percentage?.toFixed(2)}%`;
              }}
            />

            <Bar dataKey="barValue" isAnimationActive={false} radius={[2, 2, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
